import { useTranslation } from "react-i18next";
import { Flag } from "@phosphor-icons/react";

import { LandscapeChartSheet } from "../workout/LandscapeChartSheet";

/**
 * PlanLoadLandscape — профиль нагрузки по неделям во весь экран.
 *
 * ЗАЧЕМ. Развёрнутый вид есть на разборе тренировки и в линзах, а на
 * плане его не было. При этом именно здесь он нужен больше всего:
 * спарклайн в сводке недели — четыре столбика по четырнадцать пикселей,
 * и структура блока (три недели вверх, одна вниз) в нём не читается
 * физически. Ограничение то же, что в разборе: не объём экрана, а
 * ШИРИНА — повёрнутый телефон даёт 844 пикселя, и двадцать шесть недель
 * в них помещаются с запасом.
 *
 * ЧТО В ПАНЕЛИ. Глубина истории: 8 · 16 · 26 недель. Не потому, что
 * «пусть будет выбор», а потому что вопросы разные: восемь недель — это
 * текущий блок, шестнадцать — подводка к старту, двадцать шесть —
 * полсезона. Данные под большую глубину подгружаются только когда её
 * выбрали.
 */

export const PLAN_LOAD_RANGES = [8, 16, 26] as const;
export type PlanLoadRange = (typeof PLAN_LOAD_RANGES)[number];

export interface PlanLoadWeek {
  /** Понедельник недели, ISO. */
  iso: string;
  /** Короткая подпись оси — «14.07». */
  label: string;
  tss: number;
  isCurrent: boolean;
  isRace: boolean;
}

export interface PlanLoadLandscapeProps {
  open: boolean;
  onClose: () => void;
  weeks: PlanLoadWeek[];
  range: PlanLoadRange;
  onRangeChange: (r: PlanLoadRange) => void;
  loading?: boolean;
}

const CHART_MIN_H = 120;

export function PlanLoadLandscape({
  open,
  onClose,
  weeks,
  range,
  onRangeChange,
  loading = false,
}: PlanLoadLandscapeProps) {
  const { t } = useTranslation("plan");

  const loaded = weeks.filter((w) => w.tss > 0);
  const peak = Math.max(...weeks.map((w) => w.tss), 1);
  // Среднее считаем по неделям С НАГРУЗКОЙ. Пустые недели в истории —
  // это чаще всего «данных нет», а не «отдыхал»: делить на них значит
  // занижать среднее тем сильнее, чем глубже смотришь.
  const avg = loaded.length
    ? Math.round(loaded.reduce((s, w) => s + w.tss, 0) / loaded.length)
    : 0;

  return (
    <LandscapeChartSheet
      open={open}
      onClose={onClose}
      title={t("load_landscape.title", { defaultValue: "Нагрузка по неделям" })}
      actions={
        <div className="flex items-center gap-1" data-testid="plan-load-ranges">
          {PLAN_LOAD_RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => onRangeChange(r)}
              data-testid={`plan-load-range-${r}`}
              aria-pressed={r === range}
              className={[
                "px-2.5 h-9 rounded-md text-[12px] font-bold tabular-nums",
                r === range
                  ? "bg-accent text-accent-on"
                  : "bg-bg-card text-fg-secondary hover:text-fg-primary",
              ].join(" ")}
            >
              {t("load_landscape.weeks_short", { defaultValue: "{{n}} нед", n: r })}
            </button>
          ))}
        </div>
      }
    >
      <div className="h-full flex flex-col gap-2">
        <div
          className="flex-1 min-h-0 flex items-end gap-[3px]"
          style={{ minHeight: CHART_MIN_H }}
          data-testid="plan-load-bars"
        >
          {weeks.map((w) => {
            const h = w.tss > 0 ? Math.max(2, (w.tss / peak) * 100) : 0;
            return (
              <div
                key={w.iso}
                data-testid={`plan-load-week-${w.iso}`}
                data-tss={w.tss}
                className="flex-1 min-w-0 h-full flex flex-col items-center justify-end gap-1"
              >
                <span className="text-[9px] tabular-nums text-fg-secondary leading-none">
                  {w.tss > 0 ? w.tss : ""}
                </span>
                <div className="w-full flex-1 flex items-end justify-center">
                  {w.tss > 0 ? (
                    <div
                      className="w-full max-w-[34px] rounded-t-[3px]"
                      style={{
                        height: `${h}%`,
                        backgroundColor: w.isCurrent
                          ? "rgb(var(--accent, 155 165 255))"
                          : "var(--fg-muted)",
                      }}
                    />
                  ) : (
                    // Пустая неделя рисуется чертой, а не нулевым баром:
                    // ноль и «нет данных» на глаз неразличимы, а разница
                    // между ними — вся разница между отдыхом и пробелом.
                    <div className="w-full max-w-[34px] h-[2px] rounded-sm bg-bg-border" />
                  )}
                </div>
                <span className="h-3 flex items-center">
                  {w.isRace ? (
                    <Flag size={9} className="text-ok" weight="fill" aria-hidden />
                  ) : null}
                </span>
                <span
                  className={[
                    "text-[8px] tabular-nums leading-none truncate w-full text-center",
                    w.isCurrent ? "text-accent font-bold" : "text-fg-muted",
                  ].join(" ")}
                >
                  {w.label}
                </span>
              </div>
            );
          })}
        </div>

        <div
          className="shrink-0 flex items-baseline gap-4 border-t border-bg-border/60 pt-2 px-1"
          data-testid="plan-load-summary"
        >
          <span className="flex items-baseline gap-1">
            <span className="text-[15px] font-bold tabular-nums text-fg-primary leading-none">
              {avg}
            </span>
            <span className="text-[9px] uppercase tracking-[0.14em] text-fg-muted font-bold">
              {t("load_landscape.avg", { defaultValue: "средняя неделя" })}
            </span>
          </span>
          <span className="flex items-baseline gap-1">
            <span className="text-[15px] font-bold tabular-nums text-fg-primary leading-none">
              {peak}
            </span>
            <span className="text-[9px] uppercase tracking-[0.14em] text-fg-muted font-bold">
              {t("load_landscape.peak", { defaultValue: "пик" })}
            </span>
          </span>
          {/* Считать среднее по неделям без нагрузки нельзя, но и молчать
              о них нельзя — иначе среднее выглядит посчитанным по всему
              периоду. */}
          {loaded.length < weeks.length && (
            <span className="text-[10px] text-fg-muted">
              {t("load_landscape.gaps", {
                defaultValue: "средняя по {{n}} неделям с данными",
                n: loaded.length,
              })}
            </span>
          )}
          {loading && (
            <span
              className="text-[10px] text-fg-muted ml-auto"
              data-testid="plan-load-loading"
            >
              {t("load_landscape.loading", { defaultValue: "подгружаю историю…" })}
            </span>
          )}
        </div>
      </div>
    </LandscapeChartSheet>
  );
}
